import useOSS from "@/hooks/useRequest/useOSS";
import { saveIdentityToORC } from "@/hooks/useRequest/useSurveyRequest";
import { IdentityTypes } from "@/interfaces/enums";
import compressorFile from "@/utils/compressorFile";
import log2Sentry from "@/utils/log2Sentry";
import readAsDataUrl, { LocaleDataUrl } from "@/utils/readAsDataUrl";
import { useTranslation } from "next-i18next";
import React, { useEffect, useState } from "react";
import Toast from "@/components/Toast";
import SurveyTitle from "../SurveyTitleV2";
import SurveyFooter from "../SurveyFooter";
import log2sensors, { SensorsEventIds } from "@/utils/log2sensors";
import clsx from "clsx";
import CorrectIcon from "./CorrectIcon";
import ContentContainer from "@/containers/common/ContentContainer";
import useInApp from "@/hooks/useIsApp";
import rfStore from "@/utils/rfStore";
import { nanoid } from "nanoid";
import { compareVersion } from "@/utils/util";
import { UploadError } from "@/utils/sentryError";
interface UploadCardProps {
	placeholderTitle: string;
	placeholderText: string;
	children: React.ReactNode;
	bgClassName: string;
}
interface UploadIDCardProps {
	answerFieldToValueMap: Map<string, string>;
	ocrResult?: number;
	identityFirstImage?: string;
	identitySecondImage?: string;
	ocrIdentity?: number;
	onNextClick: () => void;
}
enum IdCardType {
	FRONT = "FRONT",
	BACK = "BACK",
}

function UploadIDCard(props: UploadIDCardProps) {
	const { t } = useTranslation("survey");
	const isInApp = useInApp();

	const { upload, preview } = useOSS("identityProof");
	const [activeFile, setActiveFile] = useState(-1);

	const [frontFile, setFrontFile] = useState<File>();
	const [backFile, setBackFile] = useState<File>();
	const [frontPreview, setFrontPreview] = useState<LocaleDataUrl>();
	const [backPreview, setBackPreview] = useState<LocaleDataUrl>();
	const [identity, setIdentity] = useState<IdentityTypes>();
	const [loading, setLoading] = useState(false);
	const [disabled, setDisabled] = useState(true);

	useEffect(() => {
		const _ocrIdentity = props.ocrIdentity || "";
		const _answerType = props.answerFieldToValueMap.get("documentType") || "";
		const _documentType = _answerType || _ocrIdentity;

		setIdentity(Number(_documentType));
	}, [props.answerFieldToValueMap, props.ocrIdentity]);

	useEffect(() => {
		if (!identity) return setDisabled(true);
		const firstDisabled = !props.identityFirstImage && !frontFile;
		if (identity === IdentityTypes.PASSPORT) {
			setDisabled(firstDisabled);
			return;
		}
		const secondDisabled = !props.identitySecondImage && !backFile;
		setDisabled(firstDisabled || secondDisabled);
	}, [backFile, frontFile, identity, props.identityFirstImage, props.identitySecondImage]);

	const handleConfirm = async () => {
		try {
			// if (hasCommitOCR) return props.onNextClick();
			if (disabled) return Toast.info(t("please-upload-file"));
			if (!identity) throw Error("missing identity");

			let frontUrl = "",
				backUrl = "";

			setLoading(true);
			if (props.identityFirstImage) {
				frontUrl = props.identityFirstImage;
			}

			if (frontFile) {
				try {
					frontUrl = await upload(frontFile ? frontFile : dataUrlToFile(frontPreview, nanoid() + ".png"));
				} catch (error) {
					console.log("error", error);
				}
			}

			if (identity === IdentityTypes.ID_CARD || identity === IdentityTypes.ALIEN_ID_CARD) {
				if (props.identitySecondImage) {
					backUrl = props.identitySecondImage;
				}
				if (backFile) {
					try {
						backUrl = await upload(backFile.name ? backFile : dataUrlToFile(backPreview, nanoid() + ".png"));
					} catch (error) {
						console.log("error", error);
					}
				}
			}

			await saveIdentityToORC({
				identityFirstImage: frontUrl,
				identityType: identity,
				identitySecondImage: backUrl,
			});
			props.onNextClick();

			if (!frontUrl) {
				log2Sentry(new UploadError("missing frontUrl"), "warning", {
					frontFile,
				});
			}

			if (identity === IdentityTypes.ID_CARD || identity === IdentityTypes.ALIEN_ID_CARD) {
				if (!backUrl) {
					log2Sentry(new UploadError(`missing backUrl: warning, backFile: ${JSON.stringify(backFile)}`));
				}
			}
		} catch (error: any) {
			log2Sentry(error);
		} finally {
			setLoading(false);

			if (props.ocrIdentity === IdentityTypes.PASSPORT) {
				log2sensors(SensorsEventIds.AO_UPLOADPASSPORT3_NEXTPAGE);
			} else if (props.ocrIdentity === IdentityTypes.ID_CARD) {
				log2sensors(SensorsEventIds.AO_UPLOADIDCARD3_NEXTPAGE);
			} else {
				log2sensors(SensorsEventIds.AO_UPLOADALIEN3_NEXTPAGE);
			}
		}
	};

	const dataUrlToFile = (dataUrl: any, fileName: string): File => {
		const arr = dataUrl.split(",");
		const mime = arr[0].match(/:(.*?);/)?.[1] || "";
		const bstr = atob(arr[1]);
		let n = bstr.length;
		const u8arr = new Uint8Array(n);

		while (n--) {
			u8arr[n] = bstr.charCodeAt(n);
		}

		return new File([u8arr], fileName, { type: mime });
	};

	const readFile = async (_file: File, type: IdCardType) => {
		if (!_file) return;
		const compressFile = await compressorFile(_file);
		const dataUrl = await readAsDataUrl(compressFile);
		if (rfStore.getRFState()?.userId === "7325831014222922432") {
			log2Sentry(new UploadError("readFile error"), "warning", {
				_file,
				type,
				compressFile,
				dataUrl,
			});
		}

		if (type === IdCardType.FRONT) {
			setFrontFile(compressFile);
			setFrontPreview(dataUrl);
			return;
		}
		setBackFile(compressFile);
		setBackPreview(dataUrl);
	};

	const handleInputClick = (
		identity: IdentityTypes,
		direction: 0 | 1,
		e: React.MouseEvent<HTMLInputElement, MouseEvent>,
	) => {
		if (!isInApp) return;
		if (rfStore.getRFState()?.model === "android") return;
		// 需要版本>= 6.1.0
		if (compareVersion(rfStore.getRFState()?.appVersion || "", "6.1.0") < 0) return;
		/// - ios 打开原生选择图片
		e.preventDefault();
		window.webkit.messageHandlers.customFileChoose.postMessage({
			identity,
			direction,
		});
	};

	useEffect(() => {
		// 这里注入全局函数 用户 ios 选择图片后回调
		window.onCustomFileSelected = (base64: string, direction: number) => {
			const file = base64ToFile(base64);

			const _direction = direction === 0 ? IdCardType.FRONT : IdCardType.BACK;
			readFile(file, _direction);
		};
	}, []);

	const base64ToFile = (base64String: string) => {
		// 将 Base64 编码的字符串转换为二进制数据
		const byteCharacters = atob(base64String.split(",")[1]);
		const mimeString = base64String.split(",")[0].split(":")[1].split(";")[0];
		const byteNumbers = new Array(byteCharacters.length);
		for (let i = 0; i < byteCharacters.length; i++) {
			byteNumbers[i] = byteCharacters.charCodeAt(i);
		}
		const byteArray = new Uint8Array(byteNumbers);

		// 创建 File 对象
		return new File([byteArray], nanoid() + ".png", { type: mimeString });
	};

	const getFontClassName = () => {
		if (identity === IdentityTypes.ID_CARD || identity === IdentityTypes.ALIEN_ID_CARD) {
			return `bg-[url('https://web-cdn.rockflow.tech/h5/survey/upload-front-light.png')] bg-center bg-no-repeat bg-contain dark:bg-[url('https://web-cdn.rockflow.tech/h5/survey/upload-front-dark.png')]`;
		}

		return `bg-[url('https://web-cdn.rockflow.tech/h5/survey/upload-passport-light.png')] bg-center bg-no-repeat bg-contain dark:bg-[url('https://web-cdn.rockflow.tech/h5/survey/upload-passport-dark.png')]`;
	};
	const getBackClassName = () => {
		return `bg-[url('https://web-cdn.rockflow.tech/h5/survey/upload-back-light.png')] bg-center bg-no-repeat bg-contain dark:bg-[url('https://web-cdn.rockflow.tech/h5/survey/upload-back-dark.png')]`;
	};
	const renderTitle = () => {
		if (identity === IdentityTypes.ID_CARD || identity === IdentityTypes.ALIEN_ID_CARD) {
			return t("submitting-both-sides-of-your-id-card", { type: renderType() });
		}
		return t("submitting-your-passport");
	};
	const renderType = () => {
		if (identity === IdentityTypes.ID_CARD) {
			return t("id-card");
		}
		if (identity === IdentityTypes.ALIEN_ID_CARD) {
			return t("alien-id-card");
		}
		return t("passport");
	};

	const renderFrondText = () => {
		if (identity === IdentityTypes.PASSPORT) {
			return t("upload-password");
		}
		return t("front-side");
	};

	const renderSubTitle = () => {
		const nationality = props.answerFieldToValueMap.get("nationality");

		if (nationality === "CN" && identity === IdentityTypes.ID_CARD) {
			return t("ensure-the-photo-is-original");
		} else if (nationality !== "CN" && identity === IdentityTypes.ID_CARD) {
			return t("your-uploaded-document-must-be-government-issued-and-contain-key-details");
		} else {
			return t("please-upload-clear-complete-and-watermark-free-photos-of-your-official-documents");
		}
	};

	useEffect(() => {
		const _answerType = props.answerFieldToValueMap.get("documentType") || "";
		const _identity = props.ocrIdentity || Number(_answerType);
		if (_identity === IdentityTypes.PASSPORT) {
			log2sensors(SensorsEventIds.AO_UPLOADPASSPORT3_VIEW);
		} else if (_identity === IdentityTypes.ID_CARD) {
			log2sensors(SensorsEventIds.AO_UPLOADIDCARD3_VIEW);
		} else {
			log2sensors(SensorsEventIds.AO_UPLOADALIEN3_VIEW);
		}
	}, []);

	const isWebFile = () => {
		if (typeof window === "undefined") return false;
		const ua = window.navigator.userAgent.toLowerCase();
		return ua.includes("huaweiang-an00") || ua.includes("huaweijad-al-50");
	};

	return (
		<>
			<ContentContainer
				animation
				className="px-4.5"
				header={<SurveyTitle title={renderTitle()} subtitle={renderSubTitle()} />}
				footer={
					<SurveyFooter
						showSecure={false}
						showOnfido={true}
						onNextBtnClick={handleConfirm}
						loading={loading}
						onPrivacyClick={() => {
							if (props.ocrIdentity === IdentityTypes.PASSPORT) {
								log2sensors(SensorsEventIds.AO_UPLOADPASSPORT3_PRAVICY);
							} else if (props.ocrIdentity === IdentityTypes.ID_CARD) {
								log2sensors(SensorsEventIds.AO_UPLOADIDCARD3_PRAVICY);
							} else {
								log2sensors(SensorsEventIds.AO_UPLOADALIEN3_PRAVICY);
							}
						}}
						onOnfidoClick={() => {
							if (props.ocrIdentity === IdentityTypes.PASSPORT) {
								log2sensors(SensorsEventIds.AO_UPLOADPASSPORT3_ONFFIDO);
							} else if (props.ocrIdentity === IdentityTypes.ID_CARD) {
								log2sensors(SensorsEventIds.AO_UPLOADIDCARD3_ONFFIDO);
							} else {
								log2sensors(SensorsEventIds.AO_UPLOADALIEN3_ONFFIDO);
							}
						}}
					/>
				}
			>
				<UploadCard
					bgClassName={getFontClassName()}
					placeholderText={t("upload-the-front-side", { type: renderType() })}
					placeholderTitle={renderFrondText()}
				>
					<PreviewImg src={frontPreview || preview(props.identityFirstImage) || ""} />
					{!frontPreview && props.identityFirstImage ? <CorrectIcon /> : null}
					<FileInput
						hidden={false}
						onChange={(file) => readFile(file, IdCardType.FRONT)}
						direction={0}
						active={activeFile}
						identity={identity}
						useWebFile={isWebFile()}
						onClick={(e) => {
							setActiveFile(0);
							handleInputClick(identity || IdentityTypes.ID_CARD, 0, e);
						}}
					/>
				</UploadCard>

				{(identity === IdentityTypes.ID_CARD || identity === IdentityTypes.ALIEN_ID_CARD) && (
					<UploadCard
						placeholderText={t("upload-the-back-side-of-your-id-card", {
							type: renderType(),
						})}
						placeholderTitle={t("back-side")}
						bgClassName={getBackClassName()}
					>
						<PreviewImg src={backPreview || preview(props.identitySecondImage) || ""} />
						{!backPreview && props.identitySecondImage ? <CorrectIcon /> : null}
						<FileInput
							hidden={false}
							onChange={(file) => readFile(file, IdCardType.BACK)}
							direction={1}
							active={activeFile}
							identity={identity}
							useWebFile={isWebFile()}
							onClick={(e) => {
								setActiveFile(1);
								handleInputClick(identity, 1, e);
							}}
						/>
					</UploadCard>
				)}
			</ContentContainer>
		</>
	);
}

function UploadCard(props: UploadCardProps) {
	return (
		<div className="px-4 py-5 flex justify-between items-center mt-5 dark:bg-151515 bg-f6f7f9 rounded-xl">
			<div className="flex-1 mr-4 text-left font-semibold dark:text-white text-dark">
				<div className="text-base">{props.placeholderTitle}</div>
				<p className="text-xs mt-2">{props.placeholderText}</p>
			</div>
			<div
				className={clsx(
					"w-[9rem] h-[6rem] flex-shrink-0 relative overflow-hidden flex items-center justify-center",
					props.bgClassName,
				)}
			>
				{props.children}
			</div>
		</div>
	);
}

function PreviewImg({ src, retryCount = 3 }: { src?: string | ArrayBuffer; retryCount?: number }) {
	const [errorCount, setErrorCount] = useState(0);
	const [imgSrc, setImgSrc] = useState<string | ArrayBuffer | undefined>();

	useEffect(() => {
		if (!src) return;
		setImgSrc(src);
	}, [src]);

	const handleError = () => {
		if (src instanceof ArrayBuffer) return src;
		if (errorCount >= retryCount) return src;
		setImgSrc(src + "?random=" + new Date().getTime());
		setErrorCount(errorCount + 1);
	};
	if (!imgSrc) return null;

	return (
		<div className="w-[9rem] h-[6rem] relative overflow-hidden p-2">
			<div className="w-full h-full dark:bg-151515 bg-f6f7f9">
				<img src={imgSrc as string} className="w-full h-full object-contain" alt="" onError={handleError} />
			</div>
		</div>
	);
}

function FileInput(_props: {
	hidden: boolean;
	direction: 0 | 1;
	active: number;
	identity?: IdentityTypes;
	useWebFile?: boolean;
	onChange: (file: File) => void;
	onClick?: React.MouseEventHandler<HTMLInputElement>;
}) {
	if (_props.hidden) return null;

	const validate = (_file: File) => {
		const isCorrectType = /image\/(png|jpg|jpeg)$/i.test(_file.type);
		if (!isCorrectType) {
			return { status: false, msg: "格式不正确" };
		}
		return { status: true, msg: "" };
	};

	const renderFileId = () => {
		if (_props.useWebFile) {
			return "";
		}
		if (_props.direction === _props.active) {
			return "nativeCustomFile";
		}
		return "";
	};

	return (
		<input
			type="file"
			accept="image/*"
			className="absolute w-full h-full top-0 left-0 opacity-0 z-[100]"
			id={renderFileId()}
			data-type={_props.identity}
			data-direction={_props.direction}
			onChange={(e) => {
				const _file = e.target.files?.[0] as File;
				if (!_file) return;
				const { status, msg } = validate(_file);
				if (!status) return Toast.info(msg);
				_props.onChange(_file);
			}}
			onClick={_props.onClick}
		/>
	);
}

export default UploadIDCard;
